import type { DocumentSummary } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CheckboxField,
  Container,
  ErrorState,
  FormField,
  IconButton,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Progress,
  Skeleton,
  Textarea,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  BedDouble,
  Key,
  Lock,
  LogIn,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan, useSession } from '@/features/auth/session';
import { ComplianceChecklist } from '@/features/compliance/ComplianceChecklist';
import { complianceGauge, reservationComplianceQueryOptions } from '@/features/compliance/queries';
import { DocumentPreviewDialog } from '@/features/documents/DocumentPreviewDialog';
import { ReservationErrorNotice } from '@/features/reservations/ReservationErrorNotice';
import { toDateTimeInput } from '@/features/reservations/datetime';
import {
  checkInReservation,
  reservationKeys,
  reservationQueryOptions,
  updateReservation,
} from '@/features/reservations/queries';
import { taskKeys } from '@/features/tasks/queries';
import { notificationKeys } from '@/features/notifications/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/reservations/$reservationId_/check-in')({
  component: CheckInPage,
});

interface OccupantDraft {
  key: string;
  fullName: string;
  isMain: boolean;
}

let occupantCounter = 0;
const draftKey = () => `occupant-${(occupantCounter += 1)}`;

/**
 * Check-in en page pleine (SCR-040, prototype validé `08-checkin.html`).
 * Utilisable sur tablette : cartes larges, actions 44 px, pied collant.
 *
 * Règle R-001 : le check-in reste **toujours autorisé** ; une conformité
 * INCOMPLETE n'est qu'un avertissement — c'est l'émission de la facture
 * définitive que le serveur refusera (`INVOICE_COMPLIANCE_DOCUMENTS_MISSING`).
 */
function CheckInPage() {
  const { reservationId } = Route.useParams();
  const { t, i18n } = useTranslation([
    'reservations',
    'documents',
    'compliance',
    'customers',
    'common',
    'errors',
  ]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const canRead = useCan('reservation.read');
  const canCheckIn = useCan('reservation.checkin');

  const query = useQuery({ ...reservationQueryOptions(reservationId), enabled: canRead });
  const complianceQuery = useQuery(
    reservationComplianceQueryOptions(reservationId, canRead && session.can('compliance.read')),
  );

  const [occupants, setOccupants] = useState<OccupantDraft[]>([]);
  const [checkedInAt, setCheckedInAt] = useState('');
  const [earlyCheckin, setEarlyCheckin] = useState(false);
  const [keysHanded, setKeysHanded] = useState(true);
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [preview, setPreview] = useState<DocumentSummary | null>(null);

  const reservation = query.data;

  // Pré-remplissage : occupants connus, heure d'arrivée = maintenant (D-021).
  useEffect(() => {
    if (!reservation) return;
    setOccupants(
      reservation.occupants.map((occupant) => ({
        key: draftKey(),
        fullName: occupant.fullName,
        isMain: occupant.isMain,
      })),
    );
    setCheckedInAt(toDateTimeInput(new Date().toISOString()));
    setEarlyCheckin(reservation.earlyCheckinRequested);
  }, [reservation]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!reservation) throw new Error('missing');
      // `CheckInRequest` ne porte pas l'arrivée anticipée : on met d'abord la
      // réservation à jour (contrat `UpdateReservationRequest`) puis on
      // enchaîne avec la version renvoyée par le serveur.
      let version = reservation.version;
      if (earlyCheckin !== reservation.earlyCheckinRequested && session.can('reservation.update')) {
        const updated = await updateReservation(reservation.id, {
          version,
          earlyCheckinRequested: earlyCheckin,
        });
        version = updated.version;
      }
      return checkInReservation(reservation.id, {
        version,
        checkedInAt: new Date(checkedInAt).toISOString(),
        keysHanded,
        arrivalNotes: notes.trim() || null,
        occupants: occupants
          .filter((occupant) => occupant.fullName.trim().length > 0)
          .map((occupant, index) => ({
            fullName: occupant.fullName.trim(),
            isMain: occupant.isMain || index === 0,
          })),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reservationKeys.all }),
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
        queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['housekeeping'] }),
      ]);
      toast.success(t('reservations:toasts.checkIn'));
      await navigate({ to: '/reservations/$reservationId', params: { reservationId } });
    },
  });

  if (!canRead || !canCheckIn) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={120} />
          <Skeleton height={420} />
        </div>
      </Container>
    );
  }

  if (query.isError || !reservation) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const compliance = complianceQuery.data;
  const gauge = complianceGauge(compliance?.items ?? []);
  // Le statut fait foi côté serveur : `GET /reservations/:id/compliance` d'abord,
  // le résumé porté par la réservation en repli.
  const complianceStatus = compliance?.status ?? reservation.compliance.status;
  const incomplete = complianceStatus === 'INCOMPLETE';
  const notRequired = complianceStatus === 'NOT_REQUIRED';
  /** Types encore non validés, tels que renvoyés par l'API. */
  const missingTypes = compliance?.missing ?? reservation.compliance.missing;
  const alreadyCheckedIn = reservation.status !== 'CONFIRMED';
  const canSubmit = !alreadyCheckedIn && (!incomplete || acknowledged);

  // Progression : dossier + logement prêt + clés (prototype 08, colonne latérale).
  const steps = [
    { key: 'confirmed', done: true },
    { key: 'documents', done: gauge.complete },
    { key: 'resource', done: true },
    { key: 'keys', done: keysHanded },
  ];
  const doneSteps = steps.filter((step) => step.done).length;

  return (
    <Container>
      <PageHeader
        title={t('reservations:checkIn.title', { guest: reservation.guest.displayName })}
        subtitle={t('reservations:checkIn.subtitle', {
          resource: reservation.resource.name,
          date: formatDate(reservation.arrivalAt, i18n.language),
          time: formatTime(reservation.arrivalAt, i18n.language),
          nights: reservation.nights ?? 1,
          adults: reservation.adults,
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('nav:items.operations', { defaultValue: 'Opérations' }),
            link: <Link to="/today/arrivals">{t('dashboard:arrivalsCard')}</Link>,
          },
          {
            label: reservation.reference,
            link: (
              <Link to="/reservations/$reservationId" params={{ reservationId }}>
                {reservation.reference}
              </Link>
            ),
          },
          { label: t('reservations:checkIn.breadcrumb') },
        ]}
        actions={
          <Button variant="ghost" asChild>
            <Link to="/reservations/$reservationId" params={{ reservationId }}>
              <ArrowLeft className="ico ico-16" aria-hidden="true" />
              {t('reservations:checkIn.backToReservation')}
            </Link>
          </Button>
        }
      />

      {alreadyCheckedIn ? (
        <Notice tone="danger" role="alert" title={t('reservations:errors.invalidTransitionTitle')}>
          <p className="mb-0 t-13">
            {t('reservations:errors.invalidTransitionBody', {
              status: t(`reservations:status.${reservation.status}`),
            })}
          </p>
        </Notice>
      ) : incomplete ? (
        <Notice
          tone="warning"
          className="mb-5"
          icon={<AlertTriangle className="ico ico-24 notice__ico" aria-hidden="true" />}
          title={t('reservations:checkIn.complianceBannerTitle')}
          role="status"
        >
          <p className="mb-0">
            {t('reservations:checkIn.complianceBannerBody', {
              count: gauge.total - gauge.validated,
            })}{' '}
            <span className="error-code">INVOICE_COMPLIANCE_DOCUMENTS_MISSING</span>
          </p>
          {missingTypes.length > 0 ? (
            <p className="mb-0 t-13">
              {t('reservations:detail.complianceMissingTypes', {
                types: missingTypes.map((type) => t(`documents:type.${type}`)).join(' · '),
              })}
            </p>
          ) : null}
        </Notice>
      ) : null}

      <div className="grid grid--main-aside">
        <div className="stack stack--lg">
          {/* ------------------------ Documents ------------------------ */}
          {!notRequired ? (
            <Card>
              <CardHeader
                title={t('reservations:checkIn.documentsTitle')}
                icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
                iconTone="red"
                actions={
                  <Badge tone={gauge.complete ? 'green' : 'amber'} size="lg">
                    {t('compliance:gauge', {
                      validated: gauge.validated,
                      total: gauge.total,
                    })}
                  </Badge>
                }
              />
              <CardBody>
                {gauge.awaitingValidation > 0 ? (
                  <Notice tone="info" className="mb-4">
                    <p className="mb-0 t-13">
                      {t('compliance:gaugeAwaiting', { count: gauge.awaitingValidation })}
                    </p>
                  </Notice>
                ) : null}
                {complianceQuery.isPending ? (
                  <Skeleton height={140} />
                ) : complianceQuery.isError ? (
                  <Notice tone="danger" role="alert">
                    <p className="mb-0 t-13">{errorMessage(complianceQuery.error, t)}</p>
                  </Notice>
                ) : compliance ? (
                  <ComplianceChecklist
                    reservationId={reservationId}
                    compliance={compliance}
                    mandatoryOnly
                    onPreview={setPreview}
                  />
                ) : null}
              </CardBody>
            </Card>
          ) : null}

          {/* ------------------------ Occupants ------------------------ */}
          <Card>
            <CardHeader
              title={t('reservations:checkIn.occupantsTitle')}
              icon={<Users className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setOccupants((list) => [
                      ...list,
                      { key: draftKey(), fullName: '', isMain: list.length === 0 },
                    ])
                  }
                >
                  <UserPlus className="ico ico-14" aria-hidden="true" />
                  {t('reservations:checkIn.addOccupant')}
                </Button>
              }
            />
            <CardBody>
              <p className="hint mb-4">
                {t('reservations:checkIn.occupantsHint', {
                  announced: reservation.adults + reservation.children,
                  capacity: reservation.resource.capacityMax,
                })}
              </p>
              <div className="stack stack--sm">
                {occupants.map((occupant, index) => (
                  <div className="row" style={{ gap: 8 }} key={occupant.key}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <FormField
                        label={
                          occupant.isMain || index === 0
                            ? t('reservations:checkIn.mainOccupant')
                            : t('reservations:checkIn.occupantNumber', { index: index + 1 })
                        }
                      >
                        {(field) => (
                          <Input
                            id={field.id}
                            value={occupant.fullName}
                            placeholder={t('reservations:checkIn.occupantPlaceholder')}
                            onChange={(event) =>
                              setOccupants((list) =>
                                list.map((item) =>
                                  item.key === occupant.key
                                    ? { ...item, fullName: event.target.value }
                                    : item,
                                ),
                              )
                            }
                          />
                        )}
                      </FormField>
                    </div>
                    {index > 0 ? (
                      <IconButton
                        label={t('reservations:checkIn.removeOccupant')}
                        onClick={() =>
                          setOccupants((list) => list.filter((item) => item.key !== occupant.key))
                        }
                      >
                        <Trash2 className="ico ico-16" aria-hidden="true" />
                      </IconButton>
                    ) : null}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* -------------------- Arrivée & clés ----------------------- */}
          <Card>
            <CardHeader
              title={t('reservations:checkIn.arrivalTitle')}
              icon={<Key className="ico ico-16" aria-hidden="true" />}
              actions={<span className="t-12 muted">{t('reservations:checkIn.auditedHint')}</span>}
            />
            <CardBody>
              <div className="field-row mb-5">
                <FormField
                  label={t('reservations:checkIn.actualArrival')}
                  hint={t('reservations:checkIn.actualArrivalHint')}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="datetime-local"
                      value={checkedInAt}
                      onChange={(event) => setCheckedInAt(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('reservations:checkIn.agent')}>
                  {(field) => <Input id={field.id} value={session.user.fullName} readOnly />}
                </FormField>
              </div>

              <div className="stack stack--sm mb-5">
                <CheckboxField
                  label={t('reservations:checkIn.earlyCheckin')}
                  checked={earlyCheckin}
                  onCheckedChange={(checked) => setEarlyCheckin(checked === true)}
                />
                <CheckboxField
                  label={t('reservations:checkIn.keysHanded')}
                  checked={keysHanded}
                  onCheckedChange={(checked) => setKeysHanded(checked === true)}
                />
              </div>

              <FormField label={t('reservations:checkIn.notes')}>
                {(field) => (
                  <Textarea
                    id={field.id}
                    rows={3}
                    value={notes}
                    placeholder={t('reservations:checkIn.notesPlaceholder')}
                    onChange={(event) => setNotes(event.target.value)}
                  />
                )}
              </FormField>
            </CardBody>
          </Card>

          {mutation.isError ? <ReservationErrorNotice error={mutation.error} /> : null}

          {incomplete ? (
            <Notice tone="warning" title={t('reservations:checkIn.acknowledgeTitle')}>
              <p className="t-13">{t('reservations:checkIn.acknowledgeBody')}</p>
              <CheckboxField
                label={t('reservations:checkIn.acknowledgeLabel')}
                checked={acknowledged}
                onCheckedChange={(checked) => setAcknowledged(checked === true)}
              />
            </Notice>
          ) : null}

          <div className="checkin-actions">
            <Button
              variant="primary"
              size="lg"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => mutation.mutate()}
            >
              <LogIn className="ico ico-16" aria-hidden="true" />
              {t('reservations:checkIn.submit')}
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to="/reservations/$reservationId" params={{ reservationId }}>
                {t('common:actions.cancel')}
              </Link>
            </Button>
          </div>
        </div>

        {/* ---------------------- Colonne latérale ---------------------- */}
        <aside className="stack sticky-aside">
          <Card variant="raised">
            <CardHeader
              title={t('reservations:checkIn.progressTitle')}
              headingLevel={2}
              actions={
                <Badge tone={doneSteps === steps.length ? 'green' : 'amber'}>
                  {doneSteps} / {steps.length}
                </Badge>
              }
            />
            <CardBody density="tight">
              <Progress
                className="mb-4"
                size="lg"
                value={doneSteps}
                max={steps.length}
                tone={doneSteps === steps.length ? 'green' : 'amber'}
                label={t('reservations:checkIn.progressTitle')}
                valueText={`${doneSteps} / ${steps.length}`}
              />
              <ul className="stack stack--sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {steps.map((step) => (
                  <li className="row" style={{ gap: 9 }} key={step.key}>
                    <ShieldCheck
                      className="ico ico-16"
                      style={{ color: step.done ? 'var(--success)' : 'var(--danger)' }}
                      aria-hidden="true"
                    />
                    <span className="t-13">
                      {t(`reservations:checkIn.steps.${step.key}`)}
                      <span className="sr-only">
                        {step.done
                          ? ` — ${t('reservations:checkIn.stepDone')}`
                          : ` — ${t('reservations:checkIn.stepTodo')}`}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('reservations:detail.stayTitle')}
              icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
              iconTone="blue"
            />
            <CardBody density="tight">
              <dl className="dl dl--lines t-13">
                <dt>{t('reservations:columns.reference')}</dt>
                <dd>
                  <Link
                    className="cell-id"
                    to="/reservations/$reservationId"
                    params={{ reservationId }}
                  >
                    {reservation.reference}
                  </Link>
                </dd>
                <dt>{t('reservations:fields.resource')}</dt>
                <dd>{reservation.resource.name}</dd>
                <dt>{t('reservations:fields.arrival')}</dt>
                <dd>
                  {formatDate(reservation.arrivalAt, i18n.language)} ·{' '}
                  {formatTime(reservation.arrivalAt, i18n.language)}
                </dd>
                <dt>{t('reservations:fields.departure')}</dt>
                <dd>
                  {formatDate(reservation.departureAt, i18n.language)} ·{' '}
                  {formatTime(reservation.departureAt, i18n.language)}
                </dd>
                <dt>{t('reservations:fields.nights')}</dt>
                <dd>{reservation.nights ?? '—'}</dd>
                <dt>{t('reservations:detail.billingTitle')}</dt>
                <dd>{reservation.billingParty.displayName}</dd>
              </dl>

              <div className="money-strip mt-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="money-strip__cell money-strip__cell--paid">
                  <div className="money-strip__l">{t('reservations:summary.paid')}</div>
                  <div className="money-strip__v t-18">
                    {formatAmount(reservation.totals.paid, i18n.language)}
                  </div>
                </div>
                <div className="money-strip__cell money-strip__cell--due">
                  <div className="money-strip__l">{t('reservations:summary.balance')}</div>
                  <div className="money-strip__v t-18">
                    {formatAmount(reservation.totals.balance, i18n.language)}
                  </div>
                </div>
              </div>

              {reservation.totals.balance > 0 ? (
                <Button variant="secondary" block className="mt-3" asChild>
                  <Link to="/reservations/$reservationId" params={{ reservationId }}>
                    <Banknote className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.recordDeposit')}
                  </Link>
                </Button>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('reservations:checkIn.billingEffectTitle')}
              icon={<Lock className="ico ico-16" aria-hidden="true" />}
              iconTone="red"
            />
            <CardBody density="tight">
              <ul className="stack stack--sm t-13" style={{ listStyle: 'none', padding: 0 }}>
                <li>{t('reservations:checkIn.billingEffectStay')}</li>
                <li>{t('reservations:checkIn.billingEffectProforma')}</li>
                <li className="strong" style={{ color: 'var(--danger)' }}>
                  {t('reservations:checkIn.billingEffectInvoice')}
                </li>
              </ul>
            </CardBody>
          </Card>
        </aside>
      </div>

      <DocumentPreviewDialog
        document={preview}
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
      />
    </Container>
  );
}
